/**
 * AuthInterceptor
 *
 * Purpose
 * - Aligns the Angular client with OWIN-only Bearer authentication on the server.
 * - Automatically attaches Authorization: Bearer <token> to API requests (but never to the /Token login request).
 * - Adds request correlation (X-Client-Request-Id), consistent error handling, and controlled spinner behavior.
 * - Improves resiliency with a small retry policy for transient GET failures.
 *
 * Usage
 * - Registered in AppModule providers as:
 *     { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
 *   together with provideHttpClient(withInterceptorsFromDi()) so interceptors from DI are honored.
 * - No changes are required in calling services/components; HttpClient calls automatically flow through this interceptor.
 * - To control spinner visibility for a specific request, keep existing URL conventions (e.g., Ping or UpdateAdWeekGridMetadata).
 *
 * Changes made (OWIN-only Bearer alignment + DI-cycle hardening)
 * - DI-cycle fix: removed constructor DI on AuthenticationService to prevent early-bootstrap circular dependencies
 *   (GlobalErrorHandler -> HttpClient -> Interceptors -> AuthenticationService -> MatDialog -> ErrorHandler).
 *   - Token is now read directly from sessionStorage ('rpmsToken'), which AuthenticationService already rehydrates on load.
 *   - 401 handling uses a DI-safe "hardLogout" (clear storage, navigate to /login, reload) with the same end effect as doOnLogout().
 * - Preserved all previous behavior:
 *   - Skips Authorization for POST /Token (login).
 *   - Gated header attachment on discovered API baseUrl when available (assets/config capture).
 *   - Adds X-Client-Request-Id to each request for correlation.
 *   - Optional request timing logs when config.logRequests is true.
 *   - Spinner behavior and route-specific exceptions (Ping, UpdateAdWeekGridMetadata on /price-management).
 *   - Friendly error mapping and transient GET retry policy.
 *
 * Changes made (token endpoint error handling)
 * - Skip showing error dialogs AND spinner for /Token endpoint requests.
 * - The LoginComponent handles token endpoint errors by setting UI state and showing the error dialog.
 * - Retry logic only applies to GET requests (not POST /Token).
 *
 * Operational notes
 * - If you later need richer 401 handling (dialogs, toasts), ensure it does not re-introduce DI cycles during bootstrap.
 * - This interceptor captures assets/config responses to enable conditional logging and to detect the API baseUrl.
 *
 * Security note
 * - Authorization tokens are read from sessionStorage (rehydrated by AuthenticationService). The /Token request never receives Authorization.
 */

import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse
} from "@angular/common/http";
import { Observable } from "rxjs/internal/Observable";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { SpinnerService } from "src/app/shared/spinner.service";
// NOTE: DI-cycle fix: do not inject AuthenticationService here.
import { MessageService } from "src/app/shared/message.service";

// RxJS utilities used for retry and error handling
import { throwError, timer } from 'rxjs';
import { tap, catchError, retryWhen, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  // Captured from assets/config request to enable request timing logs and to discover baseUrl.
  // This preserves your previous behavior where config was captured opportunistically in the interceptor.
  config: any;

  constructor(
    private _spinnerService: SpinnerService,
    private _router: Router,
    // For consistent, user-friendly toast/dialog messages.
    private _messageService: MessageService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // NOTE (OWIN-only Bearer):
    // - The server validates Authorization: Bearer <token> via a single OWIN bearer middleware.
    // - The /Token endpoint MUST NOT carry the Authorization header (it's the login request).
    // - For all other API calls, include the Authorization header if a token exists.

    // Detect the OAuth token endpoint: POST /Token (must NOT include Authorization header)
    const isTokenEndpoint =
      (req.method === 'POST') &&
      (req.url.endsWith('/Token') || req.url.indexOf('/Token?') > -1);

    // DI-cycle safe token read:
    // Preferred path previously was AuthenticationService.getToken(); to avoid cycles during early errors,
    // read directly from sessionStorage (AuthenticationService rehydrates it on app start).
    const token = sessionStorage.getItem('rpmsToken');

    // Show spinner for most requests
    // - Keep your "Ping" exception.
    // - Also skip for CORS preflight (OPTIONS) to avoid flicker on some browsers.
    // - IMPORTANT: Skip for /Token to avoid spinner issues during login failures
    if (req.method !== 'OPTIONS' && req.urlWithParams.indexOf('Ping') === -1 && !isTokenEndpoint) {
      this._spinnerService.handleProgressSpinnerVisibility('show');
    }

    // Generate a client-side correlation id. Useful to match client logs with server logs.
    const requestId = this.generateRequestId();

    // Only attach Authorization for your API:
    // - If config.baseUrl is known, require request URL to be relative or start with baseUrl.
    // - If config not yet loaded, preserve original behavior (attach broadly except /Token).
    const isAbsolute = /^https?:\/\//i.test(req.url);
    const baseUrl: string | undefined = this.config?.baseUrl;
    const looksLikeApiCall =
      baseUrl
        ? (!isAbsolute || req.url.startsWith(baseUrl))
        : true;

    // Build headers to set (always add client request id; add Authorization when appropriate)
    const headersToSet: Record<string, string> = { 'X-Client-Request-Id': requestId };
    if (token && !isTokenEndpoint && looksLikeApiCall) {
      headersToSet.Authorization = `Bearer ${token}`;
    }

    // Clone request once with our additional headers
    req = req.clone({ setHeaders: headersToSet });

    const started = Date.now();

    // Build the base pipeline
    let pipeline = next.handle(req);

    // Only apply retry logic to GET requests (idempotent)
    // IMPORTANT: Do NOT apply retryWhen to POST /Token or other non-GET requests
    if (req.method === 'GET') {
      pipeline = pipeline.pipe(
        retryWhen(errors => this.retryStrategy(errors, req))
      );
    }

    return pipeline.pipe(
      // Success path: preserve config capture, timing logs and spinner exceptions
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const elapsed = Date.now() - started;

          // Capture config when assets/config is requested so we can log timings conditionally and know baseUrl.
          if (req.url.indexOf('assets/config') > -1) {
            this.config = event.body;
          }

          // Optional timing logs when enabled in config
          if (this.config && this.config.logRequests) {
            console.log(`Request for ${req.urlWithParams} took ${elapsed} ms. (id=${requestId})`);
          }

          // Preserve your spinner exceptions
          if (req.urlWithParams.indexOf('Ping') !== -1) {
            // Don't hide spinner for Ping
          }
          else if (this._router.routerState.snapshot.url === '/price-management' &&
            req.urlWithParams.indexOf('UpdateAdWeekGridMetadata') !== -1) {
            // Don't hide spinner while PM grids/filters are loading after AdWeek metadata save
          }
          else if (!isTokenEndpoint) {
            // default: hide spinner on completion (but not for /Token which never showed spinner)
            this._spinnerService.handleProgressSpinnerVisibility('hide');
          }
        }
      }),

      // Error path: friendlier messaging + centralized 401 handling + always hide spinner
      catchError((error: HttpErrorResponse) => {
        const correlationId =
          error.headers?.get('x-correlation-id') ||
          error.headers?.get('request-id') ||
          requestId; // fall back to client id if server didn't send one

        // SKIP all error handling for /Token endpoint - the LoginComponent handles those errors
        // by setting UI state and showing the error dialog. Handling them here causes duplicates/loops.
        if (!isTokenEndpoint) {
          if (error.status === 401) {
            // Session expired or invalid token
            // DI-cycle safe: show message and perform a hard logout (no service resolution here)
            this._messageService.onFailure(
              'Your session has expired. Please sign in again.',
              { status: error.status, correlationId }
            );
            this.hardLogout();
          } else {
            // Friendly message mapping; fall back to a generic message
            const userMessage = this.mapErrorToMessage(error);
            if (userMessage) {
              this._messageService.onFailure(userMessage, {
                status: error.status,
                correlationId
              });
            } else {
              this._messageService.onFailure('An unexpected error occurred.', {
                status: error.status,
                correlationId
              });
            }
          }

          // Hide spinner on error (but not for /Token which never showed spinner)
          this._spinnerService.handleProgressSpinnerVisibility('hide');
        }

        // Re-throw so callers can handle if needed
        return throwError(() => error);
      })
    );
  }

  /**
   * Retry strategy for transient failures on GET requests.
   * Transient = network (0), 502, 503, 504, 429 (Too Many Requests).
   * Honors Retry-After for 429 when provided by the server; otherwise uses small backoffs.
   * NOTE: This is only applied to GET requests - the conditional is handled in intercept().
   */
  private retryStrategy(errors: Observable<HttpErrorResponse>, req: HttpRequest<any>): Observable<unknown> {
    const maxRetries = 2;
    const baseDelayMs = 500;

    return errors.pipe(
      mergeMap((error, attempt) => {
        const isTransient =
          error.status === 0 ||
          error.status === 502 ||
          error.status === 503 ||
          error.status === 504 ||
          error.status === 429;

        if (!isTransient || attempt >= maxRetries) {
          return throwError(() => error);
        }

        // If server indicates Retry-After (seconds), prefer it
        const retryAfterHeader = (error.headers?.get('Retry-After') || '').trim();
        const retryAfterMs = retryAfterHeader ? Number(retryAfterHeader) * 1000 : 0;

        const backoff = retryAfterMs || baseDelayMs * (attempt + 1);
        console.warn(`Retrying ${req.method} ${req.url} in ${backoff}ms due to ${error.status}...`);
        return timer(backoff);
      })
    );
  }

  /**
   * Map common HTTP errors to user-friendly messages, parsing typical ASP.NET Web API payload shapes.
   */
  private mapErrorToMessage(error: HttpErrorResponse): string | null {
    // Network/CORS
    if (error.status === 0) return 'Unable to reach the server. Check your network connection or VPN.';

    // Forbidden
    if (error.status === 403) return 'You do not have permission to perform this action.';

    // Not found
    if (error.status === 404) return 'The requested resource was not found.';

    // Conflict
    if (error.status === 409) return 'The request could not be completed due to a conflict. Please refresh and try again.';

    // Too Many Requests (respect Retry-After when available)
    if (error.status === 429) {
      const ra = error.headers?.get('Retry-After');
      return ra
        ? `You are sending requests too quickly. Please wait ${ra} seconds and try again.`
        : 'You are sending requests too quickly. Please wait and try again.';
    }

    // Validation or bad request (common Web API shapes)
    if (error.status === 400 || error.status === 422) {
      const e = error.error;
      if (!e) return 'The server could not process the request.';

      // OAuth token error_description
      if (e.error_description) return e.error_description;

      // RFC 7807 Problem Details
      if (e.title || e.detail) return [e.title, e.detail].filter(Boolean).join(' - ');

      // Web API ModelState: { Message, ModelState: { key: [msg...] } }
      if (e.ModelState) {
        const msgs: string[] = [];
        Object.keys(e.ModelState).forEach(k => {
          const arr = e.ModelState[k];
          if (Array.isArray(arr)) arr.forEach((m: string) => msgs.push(m));
        });
        if (msgs.length) return msgs.join('\n');
      }

      // Generic message
      if (e.message) return e.message;
      return 'The request was invalid. Please review input and try again.';
    }

    // Others -> fallback to generic handler in catchError
    return null;
  }

  // Simple GUID v4 generator for request correlation
  private generateRequestId(): string {
    // eslint-disable-next-line no-bitwise
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      // eslint-disable-next-line no-bitwise
      const r = Math.random() * 16 | 0;
      // eslint-disable-next-line no-bitwise
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * DI-cycle-safe logout that mirrors AuthenticationService.doOnLogout() effects
   * without injecting services (clear storage, navigate to /login, hard reload).
   */
  private hardLogout(): void {
    try {
      // Let the error handler know we are intentionally logging out (skip logging)
      try { (window as any).__rpmsLoggingOut = true; } catch { }
      // Clear session-related storage
      try { sessionStorage.removeItem('rpmsToken'); } catch { }
      try { sessionStorage.removeItem('rpmsUser'); } catch { }
      try { sessionStorage.removeItem('rpmsUserName'); } catch { }
      try { sessionStorage.removeItem('rpmsUserFullName'); } catch { }
      try { sessionStorage.removeItem('rpmsUserRole'); } catch { }
      try { localStorage.removeItem('rpmsSessionId'); } catch { }
    } finally {
      // Navigate to login and force a reload to reset app state
      this._router.navigate(['/login']).then(() => window.location.reload());
    }
  }
}
